import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Building2, X, ChevronDown, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable Tour Operator picker.
 * - Fetches /api/tour-operators/lookup (admin + agent, no credentials)
 * - Searchable dropdown, returns full operator object via onSelect
 *
 * Props:
 *   value: tour_operator_id (string | null)
 *   onChange: (id|null) => void
 *   onSelect: (operatorObject|null) => void  -- gives the full record for auto-fill
 *   label: string (optional)
 *   placeholder: string (optional)
 *   testId: string (optional) for data-testid prefix
 */
const TourOperatorSelect = ({
  value,
  onChange,
  onSelect,
  label = 'Tour Operator (Rubrica)',
  placeholder = 'Cerca T.O. dalla rubrica...',
  testId = 'tour-operator-select',
}) => {
  const [operators, setOperators] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    fetchOperators();
  }, []);

  useEffect(() => {
    // Resolve current value into a label
    if (value && operators.length > 0) {
      const found = operators.find((o) => o.id === value);
      if (found) setSelected(found);
    } else if (!value) {
      setSelected(null);
    }
  }, [value, operators]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchOperators = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tour-operators/lookup`);
      setOperators(res.data || []);
    } catch (err) {
      console.error('Lookup T.O. error', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (op) => {
    setSelected(op);
    setOpen(false);
    setQuery('');
    onChange && onChange(op.id);
    onSelect && onSelect(op);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    setQuery('');
    onChange && onChange(null);
    onSelect && onSelect(null);
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? operators.filter((o) =>
        [o.name, o.service_type, o.destinations, o.agency_code]
          .filter(Boolean)
          .some((f) => f.toLowerCase().includes(q))
      )
    : operators;

  return (
    <div ref={wrapperRef} className="relative">
      {label && <Label className="mb-1 block">{label}</Label>}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-trigger`}
        className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
          {selected ? (
            <span className="truncate text-sm">
              <span className="font-medium">{selected.name}</span>
              {selected.service_type && (
                <span className="text-slate-500"> · {selected.service_type}</span>
              )}
            </span>
          ) : (
            <span className="text-sm text-slate-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              data-testid={`${testId}-clear`}
              className="text-slate-400 hover:text-red-500 p-0.5 cursor-pointer"
              title="Rimuovi selezione"
            >
              <X className="w-4 h-4" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col"
          data-testid={`${testId}-dropdown`}
        >
          <div className="p-2 border-b border-slate-100">
            <Input
              autoFocus
              placeholder="Filtra per nome, tipologia, destinazione..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testId}-search`}
              className="h-8 text-sm"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-sm text-slate-500">Caricamento...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">
                {operators.length === 0
                  ? 'Nessun T.O. in rubrica. Aggiungili da Rubrica Tour Operator.'
                  : 'Nessun risultato'}
              </div>
            ) : (
              filtered.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  onClick={() => handlePick(op)}
                  data-testid={`${testId}-option-${op.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-start gap-2 border-b border-slate-50 last:border-0 ${
                    selected?.id === op.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <Building2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      {op.name}
                      {selected?.id === op.id && <Check className="w-3 h-3 text-blue-600" />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {[op.service_type, op.destinations, op.agency_code && `Cod. ${op.agency_code}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TourOperatorSelect;
